import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const DONORS_BASE = `${config.BASE_URL}/donor-control`;
const DONATIONS_BASE = `${config.BASE_URL}/donor-control/donations`;


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

export const getDonorDashboard = async () => {
  const res = await authFetch(`${DONORS_BASE}/dashboard`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donor dashboard"),
    );
  }
  return data;
};

export const getAllDonors = async () => {
  const res = await authFetch(`${DONORS_BASE}/all`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch donors"));
  }
  return data;
};

export const searchDonors = async (search) => {
  const params = new URLSearchParams();
  if (search != null && String(search).trim() !== "") {
    params.set("search", String(search).trim());
  }
  const q = params.toString();
  const res = await authFetch(`${DONORS_BASE}/search${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Donor search failed"));
  }
  return data;
};

export const getDonorDetails = async (id) => {
  const res = await authFetch(
    `${DONORS_BASE}/${encodeURIComponent(String(id))}/details`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donor details"),
    );
  }
  return data;
};

export const createDonor = async (body, csrfToken) => {
  const res = await authFetch(`${DONORS_BASE}/create`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(
      csrfToken ? { "X-CSRF-Token": csrfToken } : {},
    ),
    body: JSON.stringify(body),
  });
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to create donor"),
    );
  }
  return payload;
};

export const updateDonor = async (id, body, csrfToken) => {
  const res = await authFetch(
    `${DONORS_BASE}/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(body),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to update donor"),
    );
  }
  return payload;
};

/** @param {number} isActive 1 = active, 0 = inactive */
export const updateDonorStatus = async (id, isActive, csrfToken) => {
  const res = await authFetch(
    `${DONORS_BASE}/${encodeURIComponent(String(id))}/status`,
    {
      method: "PATCH",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify({ is_active: Number(isActive) === 1 ? 1 : 0 }),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to update donor status"),
    );
  }
  return payload;
};
